import React, { useCallback, useEffect, useRef } from "react";
import {
  ChatContent,
  InputContainer,
  PanelContainer,
  ScrollableContent,
} from "./__styles__/ChatPanel";
import AssistantIntro, { AssistantSuggestion } from "./AssistantIntro";
import ChatHeader from "./ChatHeader";
import ChatHistory from "./ChatHistory";
import ChatInput from "./ChatInput";
import { useChatAgent } from "./hooks/useChatAgent";

export interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  submissionTypeVersionId?: string;
  formData?: Record<string, any>;
  onFormUpdate?: (updates: any) => void;
  api?: string;
  suggestions?: AssistantSuggestion[];
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  submissionTypeVersionId,
  formData,
  onFormUpdate,
  api,
  suggestions,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    input,
    handleSubmit,
    isLoading,
    clearChat,
    setInput,
    sendMessage,
    files,
    addFiles,
    removeFile,
    audioRecordingStatus,
    recordingTime,
    isTranscribing,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
  } = useChatAgent({
    api,
    submissionTypeVersionId,
    formData,
    onFormUpdate,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setInput(suggestion);
      void sendMessage();
    },
    [setInput, sendMessage],
  );

  // Reset chat when panel closes
  useEffect(() => {
    if (!isOpen) {
      clearChat();
    }
  }, [isOpen, clearChat]);

  return (
    <PanelContainer isOpen={isOpen}>
      <ChatHeader onClose={onClose} />
      <ChatContent>
        <ScrollableContent>
          <AssistantIntro
            onSuggestionClick={handleSuggestionClick}
            suggestions={suggestions}
          />
          {messages.length > 0 && (
            <ChatHistory messages={messages} isLoading={isLoading} />
          )}
          <div ref={messagesEndRef} />
        </ScrollableContent>
        <InputContainer>
          <ChatInput
            value={input}
            onChange={e => setInput(e.target.value)}
            onSubmit={handleSubmit}
            disabled={isLoading}
            placeholder="Let's get started..."
            files={files}
            onFilesAdd={addFiles}
            onFileRemove={removeFile}
            audioRecordingStatus={audioRecordingStatus}
            recordingTime={recordingTime}
            isTranscribing={isTranscribing}
            onStartRecording={onStartRecording}
            onStopRecording={onStopRecording}
            onCancelRecording={onCancelRecording}
          />
        </InputContainer>
      </ChatContent>
    </PanelContainer>
  );
};

export default ChatPanel;
